/**
 * Attendance API Base — LEAF MODULE
 *
 * Contains `createApi()` definition, tag types, and shared type exports.
 * This file imports nothing from sibling API files, breaking the cycle.
 *
 * Endpoints are injected via:
 *  - fehlzeitenEndpoints.ts
 *  - timelineEndpoints.ts
 *  - lessonAttendanceEndpoints.ts
 *  - checkin-checkout/api/checkinCheckoutEndpoints.ts
 *
 * @module attendance/api/attendanceApiBase
 */

import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import type { CheckInOutStudent } from "../../checkin-checkout/types/checkinout";
import type { DailyLogEntry } from "../../checkin-checkout/data/attendanceDataAdapter";
import type { TimetableEvent } from "../../../hooks/useTimetableEvents";
import type { ModuleCardProps } from "../../flexband/types/flexband";

export type { DailyLogEntry } from "../../checkin-checkout/data/attendanceDataAdapter";

// ============================================================================
// Type Definitions
// ============================================================================

export interface AttendanceListArgs {
  schoolId: string | null;
  date: string;
  classIds: string[];
  search: string;
  forceAll?: boolean;
  presentOnly?: boolean;
}

export interface AttendanceActionArgs {
  schoolId: string | null;
  date: string;
  studentIds: string[];
  action: "check_in" | "check_out" | "batch_check_in" | "batch_check_out";
  method?: string;
  meta?: Record<string, unknown>;
}

export interface AttendanceAddNoteArgs {
  studentId: string;
  noteText: string;
}

export interface UndoCheckoutStudentArgs {
  schoolId: string;
  date: string;
  studentId: string;
  performedBy: string;
}

export interface CheckInOutBootstrapArgs {
  schoolId: string;
  date: string;
  teacherId?: string | null;
}

export interface CheckInOutClass {
  id: string;
  name: string;
}

export interface LessonAttendanceEntry {
  lessonId: string;
  studentId: string;
  status?: string | null;
}

export interface CheckInOutBootstrapResult {
  classes: CheckInOutClass[];
  students: CheckInOutStudent[];
  timetableEvents: TimetableEvent[];
  flexLessons: unknown[];
  flexModules: ModuleCardProps[];
  lessonAttendanceByLessonId: Record<string, LessonAttendanceEntry[]>;
  discrepancyNotesByStudent: Record<string, string>;
  dailyLogByStudent: Record<string, DailyLogEntry>;
}

// ============================================================================
// API Definition — empty endpoints, populated by injectEndpoints()
// ============================================================================

export const attendanceApi = createApi({
  reducerPath: "attendanceApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    "AttendanceList",
    "StudentAttendance",
    "Fehlzeiten",
    "AttendanceBundle",
    "SchoolClasses",
    "Timeline",
    "TimelineLessons",
    "AbsenceNotes",
    "DailyLog",
    "AttendanceSummary",
    "LessonNotes",
    "AbsenceConflicts",
    "IssueResolutions",
    "ScheduleOverrides",
  ],
  endpoints: () => ({}),
});
